
import { catchError, filter, finalize, last, switchMap, take, tap } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';

import { BehaviorSubject, Observable, throwError } from 'rxjs';

import { AuthService } from '../_services/auth/auth.service';
import { TypeConverter } from '../_core/type-converter';
import { SessionExpiredError } from '../_services/auth/auth-request-context';

declare const environmentResource: any;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private static readonly csrfHeaderName = 'X-CSRF-TOKEN';
    // Coordinates concurrent 401 responses so the browser performs one refresh call
    // and all pending requests retry with the same replacement access token.
    private isRefreshing = false;
    private refreshTokenSubject = new BehaviorSubject<string>(null);

    constructor(private readonly injector: Injector) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let token: string = null;
        let permissionHash: string = null;

        const authService = this.injector.get(AuthService);
        token = authService.getCurrentToken();

        authService.authenticatedUser$
            .pipe(
                take(1),
                tap(u => {
                    permissionHash = u && u.PermissionHash;
                })
            )
            .subscribe();

        const copiedRequest = this.attachToken(request, token, permissionHash);

        if (String.isNullOrEmpty(token)) {
            return next.handle(copiedRequest);
        }

        return next.handle(copiedRequest)
            .pipe(
                last(),
                tap(event => {
                    if(event instanceof HttpResponse) {
                        this.inspectResponse(event, authService);
                    }
                }),
                catchError(error => this.handleAuthError(error, copiedRequest, next, authService))
            );
    }
    
    //  Inspect the response packet
    private inspectResponse<T>(response: HttpResponse<any>, authService: AuthService) {
        //  Check to see if we need to refresh the permissions
        const responseHash = response.headers.get("X-Genea-User-Hash");

        if (!String.isNullOrEmpty(responseHash)) {
            authService.refresh(responseHash);
        }
    }

    private handleAuthError(error: any, request: HttpRequest<any>, next: HttpHandler, authService: AuthService): Observable<HttpEvent<any>> {
        if (!(error instanceof HttpErrorResponse)) {
            return throwError(error);
        }

        // The API has authenticated this user, but the user is not permitted to use the
        // requested resource. Refreshing the access token cannot grant a missing permission.
        if (error.status === 403) {
            return throwError(error);
        }

        if (error.status !== 401 || request.url.indexOf('/auth/token/refresh') >= 0
            || request.url.indexOf('/auth/logout') >= 0) {
            return throwError(error);
        }

        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            // Access tokens are expected to expire. Try the refresh-cookie flow once,
            // then retry the original request with the replacement access token.
            return this.refreshAccessToken(authService, this.getBearerToken(request))
                .pipe(
                    switchMap(token => {
                        this.refreshTokenSubject.next(token);
                        return this.retryAuthenticatedRequest(request, token, next, authService);
                    }),
                    catchError(refreshError => {
                        // Carry the centrally handled expiry result to every waiting request.
                        // Preserve transient refresh errors instead of misreporting the original 401.
                        this.refreshTokenSubject.error(refreshError);
                        this.refreshTokenSubject = new BehaviorSubject<string>(null);
                        return throwError(refreshError);
                    }),
                    finalize(() => {
                        this.isRefreshing = false;
                    })
                );
        }

        return this.refreshTokenSubject
            .pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => this.retryAuthenticatedRequest(request, token, next, authService))
            );
    }

    private retryAuthenticatedRequest(request: HttpRequest<any>, token: string, next: HttpHandler,
        authService: AuthService): Observable<HttpEvent<any>> {
        if (!authService.getCurrentToken()) {
            return throwError(new SessionExpiredError(null));
        }
        return next.handle(this.attachToken(request, token)).pipe(
            catchError(error => error instanceof HttpErrorResponse && error.status === 401
                ? throwError(authService.expireSession(error))
                : throwError(error))
        );
    }

    /**
     * Renews through AuthService, which serializes rotation across tabs. Passing the token this request
     * failed with lets a tab that waited reuse the replacement another tab already obtained.
     */
    private refreshAccessToken(authService: AuthService, failedAccessToken: string): Observable<string> {
        return authService.refreshAccessToken(failedAccessToken);
    }

    private getBearerToken(request: HttpRequest<any>): string {
        const authorization = request.headers.get('Authorization') || '';
        const prefix = 'Bearer ';

        return authorization.indexOf(prefix) === 0
            ? authorization.substring(prefix.length)
            : null;
    }

    private attachToken(request: HttpRequest<any>, token: string, permissionHash: string = ''): HttpRequest<any> {
        let headers = request.headers;

        if (!String.isNullOrEmpty(token)) {
            headers = headers
                .set('Authorization', `Bearer ${token}`)
                .set('X-Genea-User-Hash', permissionHash || '');
        }

        if (this.isCsrfProtectedRequest(request)) {
            const csrfToken = this.getCookieValue(this.getCsrfCookieName());

            if (!String.isNullOrEmpty(csrfToken)) {
                headers = headers.set(AuthInterceptor.csrfHeaderName, csrfToken);
            }
        }

        return request.clone({
            headers
        });
    }

    private isCsrfProtectedRequest(request: HttpRequest<any>): boolean {
        return request.url.indexOf('/auth/token/refresh') >= 0 || request.url.indexOf('/auth/logout') >= 0;
    }

    private getCsrfCookieName(): string {
        const authentication = environmentResource && environmentResource.authentication;

        return authentication && authentication.csrfCookieName
            ? authentication.csrfCookieName
            : 'genea_local_csrf_token';
    }

    private getCookieValue(name: string): string | null {
        const encodedName = `${encodeURIComponent(name)}=`;

        return document.cookie
            .split(';')
            .map(cookie => cookie.trim())
            .filter(cookie => cookie.indexOf(encodedName) === 0)
            .map(cookie => decodeURIComponent(cookie.substring(encodedName.length)))
            .firstOrDefault();
    }
}
